import React from "react";
import { TableRow, TableCell, Grid, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCheck, faUserClock, faUserXmark } from "@fortawesome/free-solid-svg-icons";

import { TnAAbsenceDTO } from "../../../../../../../models/tna";
import { StatusIndicator } from "../../../../../../MyCalendar/DetailedFullCalendar/styles";
import { LightTooltip } from "../../../../../../AttachedDocuments/ButtonWithBadge/CustomTooltip/CustomTooltip";
import { intlFormatDateMonth, intlFormatDateShort } from "../../../../../../../utils/time-utils";
import { CustomDate } from "../../../../../../../models/custom-date/custom-date";
import { QuestionIcon } from "../../../../../../../assets/icons";
import CommentBubble from "../../../../../../../reusable-components/CommentBubble/CommentBubble";
import AttachedDocuments from "../../../../../../AttachedDocuments/AttachedDocuments";

interface Props {
  absence: TnAAbsenceDTO;
}

const OverlappingAbsencesRow = ({ absence }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_absence";
  const theme = useTheme();

  return (
    <TableRow>
      <TableCell align={"center"}>
        <StatusIndicator
          isRejected={absence.isRejected}
          isCancelled={absence.isCancelled}
          isApproved={absence.isApproved}
          key={absence.id}
        >
          <Grid
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: "5px",
              position: "relative",
            }}
          >
            <LightTooltip
              title={
                <Grid sx={{ display: "flex", flexDirection: "column", maxWidth: "300px" }}>
                  <Typography>
                    {absence.absenceType?.name +
                      ": " +
                      (absence.fromDate?.year !== absence.toDate?.year ||
                      absence.fromDate?.month !== absence.toDate?.month ||
                      absence.fromDate?.day !== absence.toDate?.day
                        ? `${intlFormatDateMonth(CustomDate.ToDate(absence.fromDate) as Date)} - ${intlFormatDateMonth(
                            CustomDate.ToDate(absence.toDate) as Date
                          )}`
                        : `${intlFormatDateMonth(CustomDate.ToDate(absence.fromDate) as Date)}`)}
                  </Typography>

                  <Typography>
                    Created by: <b>{absence.createdByUser?.name ?? absence.createdByUser?.email ?? ""}</b>
                  </Typography>

                  {absence.modifiedByUser && (absence.isApproved || absence.isRejected) && !absence.isCancelled && (
                    <>
                      {absence.isApproved && (
                        <Typography>
                          Approved by:{" "}
                          <b>{absence.acceptedRejectedByUser?.name ?? absence.modifiedByUser?.name ?? ""}</b>
                        </Typography>
                      )}

                      {absence.isRejected && (
                        <Typography>
                          Rejected by:{" "}
                          <b>{absence.acceptedRejectedByUser?.name ?? absence.modifiedByUser?.name ?? ""}</b>
                        </Typography>
                      )}
                    </>
                  )}
                </Grid>
              }
            >
              <Typography>
                {`${absence.absenceType?.code}${
                  !absence.isRejected && !absence.isCancelled && !absence.isApproved ? " ?" : ""
                }`}
              </Typography>
            </LightTooltip>

            {!!absence.countedDurationDays && (
              <Typography
                variant="body2"
                title={`Counted days :  ${absence.countedDurationDays} day${
                  absence.countedDurationDays > 1 ? "s" : ""
                }/ Total absence days : ${absence.calendaristicDays} day${absence.calendaristicDays > 1 ? "s" : ""}`}
              >{` ${absence.countedDurationDays} day${absence.countedDurationDays > 1 ? "s" : ""} `}</Typography>
            )}

            <React.Fragment>
              {!!absence.cancelRequested &&
                !!absence.cancelRequestedAt &&
                absence.cancellationRequestApproved === null && (
                  <QuestionIcon
                    style={{ width: "20px", height: "20px", position: "absolute", right: "-8px", top: "-8px" }}
                    title={`Requested absence cancellation on ${intlFormatDateShort(
                      new Date(absence.cancelRequestedAt)
                    )}`}
                  />
                )}
              {absence.needAcceptance &&
                (absence.acceptedByClient ? (
                  <FontAwesomeIcon
                    icon={faUserCheck}
                    color={"greenyellow"}
                    title={`Accepted by client ${
                      absence.acceptedRejectedByUser?.name ?? absence.acceptedRejectedByEmail
                    }`}
                  />
                ) : absence.rejectedByClient ? (
                  <FontAwesomeIcon
                    icon={faUserXmark}
                    color={theme.palette.error.main}
                    title={`Rejected by client ${
                      absence.acceptedRejectedByUser?.name ?? absence.acceptedRejectedByEmail
                    }`}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faUserClock}
                    color={theme.palette.text.secondary}
                    title={"Pending client acceptance"}
                  />
                ))}
            </React.Fragment>
          </Grid>
        </StatusIndicator>
      </TableCell>

      <TableCell align={"center"}>
        <Typography>{absence.employee?.name ?? ""}</Typography>
      </TableCell>

      <TableCell align={"center"}>
        <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          {absence?.absenceType?.name && <Typography>{absence.absenceType.name}</Typography>}
          {absence?.comment && (
            <CommentBubble
              title={`${t(`${tRP}.fields.comment`)}:`}
              comment={absence.comment}
            />
          )}
        </Grid>
      </TableCell>

      <TableCell align={"center"}>
        <Typography>
          {absence?.fromDate && (
            <Typography>
              {intlFormatDateShort(new Date(absence.fromDate.year, absence.fromDate.month - 1, absence.fromDate.day))}
            </Typography>
          )}
        </Typography>
      </TableCell>

      <TableCell align={"center"}>
        <Typography>
          {absence?.toDate && (
            <Typography>
              {intlFormatDateShort(new Date(absence.toDate.year, absence.toDate.month - 1, absence.toDate.day))}
            </Typography>
          )}
        </Typography>
      </TableCell>

      <TableCell align={"center"}>
        <Grid
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            height: "50px",
          }}
        >
          {!!absence?.documentation && (
            <AttachedDocuments
              count={absence.documentation?.filesCount}
              documentIDs={[absence.documentation?.id]}
              buttonSize={"2x"}
              documentsReadOnlyFields={[
                {
                  documentID: absence.documentation?.id,
                  readOnlyFields: {
                    isEmployeeRO: true,
                    isCategoryRO: true,
                    isLegalEntityRO: true,
                    isTypeRO: true,
                    isDateRO: true,
                    isStatusRO: true,
                  },
                },
              ]}
              hideReadOnlyFields
              useSimpleView
              documentIsReadOnly
            />
          )}
        </Grid>
      </TableCell>

      <TableCell align={"center"}>
        <Typography>
          {absence?.isApproved && !absence?.isCancelled ? (
            <Typography title={`Approved by ${absence?.modifiedByUser?.name}`}>
              {absence?.modifiedByUser?.name}
            </Typography>
          ) : absence?.isRejected && !absence?.isCancelled ? (
            <Typography title={`Rejected by ${absence?.modifiedByUser?.name}`}>
              {absence?.modifiedByUser?.name}
            </Typography>
          ) : (
            <></>
          )}
        </Typography>
      </TableCell>

      <TableCell align={"center"}>
        <Grid sx={{ display: "flex", flexDirection: "row", gap: "5px", flexWrap: "wrap" }}>
          {absence?.acceptedRejectedByUser?.name && <Typography>{absence.acceptedRejectedByUser.name}</Typography>}
          {absence?.acceptedByClient ? (
            <Typography>
              <FontAwesomeIcon
                icon={faUserCheck}
                color={theme.palette.success.main}
              />
            </Typography>
          ) : absence?.rejectedByClient ? (
            <Typography>
              <FontAwesomeIcon
                icon={faUserXmark}
                color={theme.palette.error.main}
              />
            </Typography>
          ) : (
            <></>
          )}
        </Grid>
      </TableCell>
    </TableRow>
  );
};

export default OverlappingAbsencesRow;
